import {
	Bot,
	Check,
	Clock,
	Copy,
	Loader2,
	Send,
	User,
	Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import {
	type ChatCompletionParams,
	createAssistantMessage,
	createUserMessage,
	loadConversation,
	type PlaygroundMessage,
	saveConversation,
	streamChatCompletion,
	toApiMessages,
} from "../../services/playground";
import type { RouteConfig } from "../../services/route-configs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface PlaygroundChatProps {
	authToken: string | null;
	routeConfig: RouteConfig | null;
	systemPrompt?: string;
	params?: ChatCompletionParams;
	isReady?: boolean;
	onMessagesChange?: (hasMessages: boolean) => void;
	refreshKey?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// MESSAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function ChatMessageBubble({
	message,
	isStreaming,
}: {
	message: PlaygroundMessage;
	isStreaming?: boolean;
}) {
	const [copied, setCopied] = useState(false);
	const isUser = message.role === "user";

	const handleCopy = async () => {
		await navigator.clipboard.writeText(message.content);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div
			className={cn(
				"flex gap-3 p-4 rounded-lg",
				isUser ? "bg-primary/5" : "bg-muted/50",
			)}
		>
			{/* Avatar */}
			<div
				className={cn(
					"flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
					isUser
						? "bg-primary text-primary-foreground"
						: "bg-muted-foreground/20",
				)}
			>
				{isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 mb-1">
					<span className="text-sm font-medium">
						{isUser ? "You" : "Assistant"}
					</span>
					<span className="text-xs text-muted-foreground">
						{message.timestamp.toLocaleTimeString()}
					</span>
					{isStreaming && (
						<Loader2 className="h-3 w-3 animate-spin text-primary" />
					)}
				</div>

				{/* Message content */}
				<div className="text-sm whitespace-pre-wrap break-words">
					{message.content ||
						(isStreaming && (
							<span className="text-muted-foreground italic">
								Generating...
							</span>
						))}
				</div>

				{/* Metadata for assistant messages */}
				{!isUser && !isStreaming && message.content && (
					<div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
						{message.cached && (
							<Badge variant="outline" className="text-[10px] gap-1">
								<Zap className="h-3 w-3" />
								{message.cacheLayer === "hot" ? "Hot Cache" : "Semantic Cache"}
							</Badge>
						)}
						{message.responseTimeMs && (
							<span className="flex items-center gap-1">
								<Clock className="h-3 w-3" />
								{message.responseTimeMs}ms
							</span>
						)}
						{message.tokenUsage && (
							<span>{message.tokenUsage.total_tokens} tokens</span>
						)}
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10"
							onClick={handleCopy}
							aria-label="Copy message"
						>
							{copied ? (
								<Check className="h-4 w-4 text-green-500" />
							) : (
								<Copy className="h-4 w-4" />
							)}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function PlaygroundChat({
	authToken,
	routeConfig,
	systemPrompt,
	params,
	isReady = true,
	onMessagesChange,
	refreshKey: _refreshKey = 0,
}: PlaygroundChatProps) {
	const [messages, setMessages] = useState<PlaygroundMessage[]>([]);
	const [input, setInput] = useState("");
	const [isStreaming, setIsStreaming] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const messagesEndRef = useRef<HTMLDivElement>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// Load saved conversation when model config changes or refresh is triggered
	useEffect(() => {
		if (routeConfig?.id) {
			const saved = loadConversation(routeConfig.id);
			setMessages(saved);
			onMessagesChange?.(saved.length > 0);
		} else {
			setMessages([]);
			onMessagesChange?.(false);
		}
	}, [routeConfig?.id, onMessagesChange]);

	// Save conversation when messages change
	useEffect(() => {
		if (routeConfig?.id && messages.length > 0) {
			saveConversation(routeConfig.id, messages);
		}
		onMessagesChange?.(messages.length > 0);
	}, [messages, routeConfig?.id, onMessagesChange]);

	// Scroll to bottom on new messages
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, []);

	const handleSend = useCallback(async () => {
		if (!input.trim() || !authToken || !routeConfig || isStreaming) return;

		const tokenWithPrefix = authToken.startsWith("nova_sbx_")
			? authToken
			: `nova_sbx_${authToken}`;
		const rawJwt = tokenWithPrefix.replace("nova_sbx_", "");
		if (rawJwt.split(".").length !== 3) {
			setError("Invalid sandbox token format. Please re-login to refresh it.");
			toast.error("Invalid sandbox token", {
				description: "Please re-login to refresh the sandbox token.",
			});
			return;
		}

		const userMessage = createUserMessage(input.trim());
		const assistantMessage = createAssistantMessage();

		setMessages((prev) => [...prev, userMessage, assistantMessage]);
		setInput("");
		setError(null);
		setIsStreaming(true);

		const startTime = Date.now();
		// Get model identifier from primary model config
		const primaryModelConfig = routeConfig.models?.find(
			(m) => m.isPrimary,
		)?.modelConfig;
		if (!primaryModelConfig) {
			toast.error("No primary model config found in route config");
			return;
		}
		const model = primaryModelConfig.modelIdentifier;
		const apiMessages = toApiMessages([...messages, userMessage]);

		// Use streaming
		const cappedParams = {
			...params,
			max_tokens:
				params?.max_tokens && params.max_tokens > 2048
					? 2048
					: params?.max_tokens,
		};

		const controller = streamChatCompletion(
			tokenWithPrefix,
			model,
			apiMessages,
			{
				...cappedParams,
				nova: {
					...cappedParams?.nova,
					modelConfigId: routeConfig.id,
					systemPromptOverride: systemPrompt || undefined,
				},
			},
			{
				onToken: (token) => {
					setMessages((prev) => {
						const updated = [...prev];
						const last = updated[updated.length - 1];
						if (last && last.role === "assistant") {
							updated[updated.length - 1] = {
								...last,
								content: last.content + token,
							};
						}
						return updated;
					});
				},
				onComplete: (fullResponse, usage) => {
					const responseTime = Date.now() - startTime;
					setMessages((prev) => {
						const updated = [...prev];
						const last = updated[updated.length - 1];
						if (last && last.role === "assistant") {
							updated[updated.length - 1] = {
								...last,
								content: fullResponse,
								responseTimeMs: responseTime,
								tokenUsage: usage,
							};
						}
						return updated;
					});
					setIsStreaming(false);
					abortControllerRef.current = null;
				},
				onError: (err) => {
					setError(err.message);
					setIsStreaming(false);
					abortControllerRef.current = null;
					// Remove the empty assistant message
					setMessages((prev) => prev.slice(0, -1));
				},
			},
		);

		abortControllerRef.current = controller;
	}, [
		input,
		authToken,
		routeConfig,
		messages,
		systemPrompt,
		params,
		isStreaming,
	]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleStop = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			setIsStreaming(false);
		}
	};

	const ready = Boolean(authToken && routeConfig && isReady);

	return (
		<div className="flex flex-col h-full">
			{/* Messages Area */}
			<div className="flex-1 overflow-y-auto p-4 space-y-4">
				{!ready ? (
					<div className="flex flex-col items-center justify-center h-full text-center">
						<Bot className="h-12 w-12 text-muted-foreground/50 mb-4" />
						<h3 className="text-lg font-medium text-muted-foreground">
							Select a Model to Start
						</h3>
						<p className="text-sm text-muted-foreground mt-2 max-w-md">
							Pick a model configuration above. Sandbox token is applied
							automatically—no API key selection needed.
						</p>
					</div>
				) : messages.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-full text-center">
						<Bot className="h-12 w-12 text-muted-foreground/50 mb-4" />
						<h3 className="text-lg font-medium">
							Chat with {routeConfig?.name || "Route Config"}
						</h3>
						<p className="text-sm text-muted-foreground mt-2 max-w-md">
							Start a conversation to test your route configuration.
							{routeConfig?.hotCacheEnabled && " Hot caching is enabled."}
							{routeConfig?.semanticCacheEnabled &&
								" Semantic caching is enabled."}
						</p>
					</div>
				) : (
					messages.map((msg, idx) => (
						<ChatMessageBubble
							key={msg.id}
							message={msg}
							isStreaming={
								isStreaming &&
								idx === messages.length - 1 &&
								msg.role === "assistant"
							}
						/>
					))
				)}
				<div ref={messagesEndRef} />
			</div>

			{/* Error Display */}
			{error && (
				<div className="mx-4 mb-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
					{error}
				</div>
			)}

			{/* Input Area */}
			<div className="border-t border-border p-4 pb-4">
				<div className="flex items-stretch gap-3">
					<div className="flex-1 relative">
						<Textarea
							ref={textareaRef}
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={
								ready
									? "Type a message... (Enter to send, Shift+Enter for newline)"
									: "Select a model to start"
							}
							disabled={!ready || isStreaming}
							className="min-h-[80px] max-h-[300px] resize-none text-base py-3 px-4 border-border h-[80px]"
							rows={3}
						/>
					</div>
					{isStreaming ? (
						<Button
							variant="destructive"
							size="icon"
							className="h-[80px] w-14 flex-shrink-0 rounded-md shadow-sm hover:shadow transition-all"
							onClick={handleStop}
							aria-label="Stop generation"
						>
							<Loader2 className="h-4 w-4 animate-spin" />
						</Button>
					) : (
						<Button
							size="icon"
							className="h-[80px] w-14 flex-shrink-0 rounded-md bg-primary/90 hover:bg-primary text-primary-foreground shadow-sm hover:shadow transition-all disabled:opacity-40 disabled:cursor-not-allowed"
							onClick={handleSend}
							disabled={!ready || !input.trim()}
							aria-label="Send message"
						>
							<Send className="h-4 w-4" />
						</Button>
					)}
				</div>
				{ready &&
					routeConfig &&
					(() => {
						const primaryModelConfig = routeConfig.models?.find(
							(m) => m.isPrimary,
						)?.modelConfig;
						if (!primaryModelConfig) return null;
						return (
							<p className="text-xs text-muted-foreground mt-2">
								Using{" "}
								<span className="font-medium">
									{primaryModelConfig.providerType}
								</span>{" "}
								/{" "}
								<span className="font-mono">
									{primaryModelConfig.modelIdentifier}
								</span>
							</p>
						);
					})()}
			</div>
		</div>
	);
}
